import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import {
  Category,
  Subcategory,
  Product,
  fetchAllCategories,
  getSoftwareCategory,
  getProductServiceCategory,
  generateSlug,
  findSubcategoryBySlug as findSubcategoryBySlugUtil,
  findProductBySlug as findProductBySlugUtil
} from '../services/api';
import { useTranslatedData } from '../hooks/useTranslatedData';

interface ProductContextType {
  categories: Category[];
  softwareCategory: Category | undefined;
  productServiceCategory: Category | undefined;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  findSubcategoryBySlug: (categoryType: 'software' | 'product and service', slug: string) => Subcategory | undefined;
  findProductBySlug: (subcategory: Subcategory, productSlug: string) => Product | undefined;
  generateSlug: (name: string) => string;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

interface ProductProviderProps {
  children: ReactNode;
}

export const ProductProvider: React.FC<ProductProviderProps> = ({ children }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAllCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Translate all categories data automatically
  // The hook handles memoization internally
  const translatedCategories = useTranslatedData(categories) || [];

  // Memoize derived values to prevent unnecessary recalculations
  const softwareCategory = useMemo(() => getSoftwareCategory(translatedCategories), [translatedCategories]);
  const productServiceCategory = useMemo(() => getProductServiceCategory(translatedCategories), [translatedCategories]);

  // IMPORTANT: Use original (untranslated) categories for slug matching
  // This ensures slugs remain consistent across language changes
  const findSubcategoryBySlug = useCallback(
    (categoryType: 'software' | 'product and service', slug: string) => {
      // Find using original categories to ensure slug consistency
      const originalSubcategory = findSubcategoryBySlugUtil(categories, categoryType, slug);
      if (!originalSubcategory) return undefined;

      // Return the translated version by finding the matching subcategory in translated data
      const translatedCategory = translatedCategories.find(cat => cat.title === categoryType);
      if (!translatedCategory) return originalSubcategory;

      return translatedCategory.subcategories.find(sub => sub.id === originalSubcategory.id) || originalSubcategory;
    },
    [categories, translatedCategories]
  );

  const findProductBySlug = useCallback(
    (subcategory: Subcategory, productSlug: string) => {
      return findProductBySlugUtil(subcategory, productSlug);
    },
    []
  );

  const value: ProductContextType = {
    categories: translatedCategories,
    softwareCategory,
    productServiceCategory,
    loading,
    error,
    refetch: fetchData,
    findSubcategoryBySlug,
    findProductBySlug,
    generateSlug,
  };

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = (): ProductContextType => {
  const context = useContext(ProductContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductProvider');
  }
  return context;
};

export default ProductContext;

